import React from 'react';
import { useTranslation } from 'react-i18next';
import { Target, Eye, Heart, Award } from 'lucide-react';

const AboutSection = () => {
  const { i18n } = useTranslation();

  const aboutItems = [
    {
      icon: Eye,
      titleAr: 'رؤيتنا',
      titleEn: 'Our Vision',
      descriptionAr:
        'أن نكون الخيار القانوني الأول للأفراد وقطاع الأعمال من خلال تقديم خدمات قانونية مهنية موثوقة، ترتكز على الجودة، والابتكار، والاحترافية.',
      descriptionEn:
        'To be the first legal choice for individuals and the business sector by delivering trusted professional legal services grounded in quality, innovation, and professionalism.',
      gradient: 'from-wazin-gold to-wazin-gold-600'
    },
    {
      icon: Target,
      titleAr: 'رسالتنا',
      titleEn: 'Our Mission',
      descriptionAr:
        'تقديم حلول قانونية متكاملة تساعد عملاءنا على تحقيق أهدافهم وحماية حقوقهم، من خلال فريق من المحامين والمستشارين المتخصصين، مع الالتزام بالشفافية، والدقة، والموثوقية في كل ما نقدمه.',
      descriptionEn:
        'To provide integrated legal solutions that help our clients achieve their objectives and protect their rights through a team of specialized lawyers and consultants, with a commitment to transparency, precision, and reliability in everything we deliver.',
      gradient: 'from-wazin-navy to-wazin-navy-700'
    },
    {
      icon: Heart,
      titleAr: 'قيمنا',
      titleEn: 'Our Values',
      descriptionAr:
        'نؤمن بأن العمل القانوني الرصين يقوم على الشفافية، والدقة الإجرائية، والالتزام المهني، والخصوصية، والعمل بروح الفريق، بما يضمن تقديم خدمة قانونية موثوقة ومستدامة.',
      descriptionEn:
        'We believe that sound legal practice is built on transparency, procedural accuracy, professional commitment, confidentiality, and teamwork, ensuring dependable and sustainable legal service.',
      gradient: 'from-wazin-gold to-wazin-gold-600'
    },
    {
      icon: Award,
      titleAr: 'إنجازاتنا',
      titleEn: 'Our Achievements',
      descriptionAr:
        'نعتز بسجل مهني قائم على الخبرة العملية، والبناء القانوني الدقيق، وتقديم حلول قانونية موثوقة في عدد من المجالات، بما يعكس التزامنا بالجودة والاحتراف في خدمة عملائنا.',
      descriptionEn:
        'We take pride in a professional track record built on practical experience, precise legal structuring, and the delivery of trusted legal solutions across multiple areas, reflecting our commitment to quality and professionalism in serving our clients.',
      gradient: 'from-wazin-navy to-wazin-navy-700'
    }
  ];

  return (
    <section className="py-20 px-4 bg-gray-50" id="about">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-wazin-navy mb-4">
            {i18n.language === 'ar' ? 'من نحن' : 'About Us'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {i18n.language === 'ar'
              ? 'نحن شركة قانونية رائدة نسعى لتقديم خدمات قانونية متخصصة بمعايير مهنية عالية، تجمع بين الخبرة العملية، والدقة في البناء القانوني، والحرص على تحقيق مصالح العملاء بكفاءة وموثوقية.'
              : 'We are a leading legal company committed to delivering specialized legal services with high professional standards, combining practical experience, precision in legal structuring, and a dedicated focus on advancing our clients’ interests with efficiency and reliability.'
            }
          </p>
        </div>

        {/* About Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {aboutItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              >
                {/* Background Gradient on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-wazin-navy mb-4">
                    {i18n.language === 'ar' ? item.titleAr : item.titleEn}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed">
                    {i18n.language === 'ar' ? item.descriptionAr : item.descriptionEn}
                  </p>
                </div>

                {/* Decorative Element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-wazin-gold/10 rounded-full blur-2xl"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
